import { Menu } from 'lucide-react';
import * as Icons from 'lucide-react';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useNavigation } from '../../../../core/context/NavigationContext';
import { useAuth } from "../../../auth/hooks";
import { usePermissions } from '@features/core/hooks/usePermissions';
import { useGlobalUserPermission } from '@shared/hooks/useGlobalUserPermission';

interface SidenavProps {
  collapsed: boolean;
  onToggle: () => void;
}

export const sidenav: React.FC<SidenavProps> = ({ collapsed, onToggle }) => {
  const permissionData = useGlobalUserPermission();
  
  const location = useLocation();
  // const [collapsed, setCollapsed] = useState(true);
  const { navigationItems } = useNavigation(); // This now contains dynamic navigation
  const { logout } = useAuth();
  
  // Add permission check
  const { hasRouteAccess } = usePermissions();
  
  const getIcon = (iconName: string) => {
    if (!iconName) return null;
    const iconMap: { [key: string]: string } = {
      home: 'Home',
      team: 'Users',
      setting: 'Settings',
      'cloud-upload': 'Upload',
      logout: 'LogOut',
      search: 'Search',
      user: 'User',
      'file-text': 'FileText',
      database: 'Database',
      monitor: 'Monitor',
      profile: 'User',
      'check-circle': 'CheckCircle',
      'file-sync': 'FileSync',
      robot: 'Bot',
      container: 'Container',
      calendar: 'Calendar',
      'play-circle': 'PlayCircle',
      'unordered-list': 'List',
      schedule: 'Clock',
      play: 'Play',
      workflow: 'Workflow',
      mobile: 'Smartphone',
      printer: 'Printer',
      crown: 'Crown',
      block: 'Square',
      apartment: 'Building',
      upload: 'Upload',
      'credit-card': 'CreditCard',
      'plus-circle': 'PlusCircle',
      'usergroup-add': 'Users',
      tool: 'Tool',
      meh: 'Meh',
      disconnect: 'Unlink',
      'pause-circle': 'PauseCircle',
      swap: 'Swap',
      edit: 'Edit',
      wallet: 'Wallet',
      'exclamation-circle': 'AlertCircle',
      mail: 'Mail',
      dollar: 'DollarSign',
      tag: 'Tag',
      'customer-service': 'Headphones',
      'file-done': 'FileCheck',
      send: 'Send',
      'info-circle': 'Info',
      bulb: 'Lightbulb',
      phone: 'Phone',
      code: 'Code',
      form: 'FileText',
      link: 'Link',
      plus: 'Plus',
      delete: 'Trash2',
      sync: 'RefreshCw',
      camera: 'Camera',
      wifi: 'Wifi',
      'cloud-server': 'Server',
      'dollar-circle': 'CircleDollarSign',
      'file-protect': 'Shield',
      qrcode: 'QrCode',
      car: 'Car',
      function: 'Function',
      'appstore-add': 'AppWindow',
      branches: 'GitBranch',
      ci: 'Activity',
      warning: 'AlertTriangle',
      control: 'Sliders',
      environment: 'Map',
      barcode: 'Barcode',
      layout: 'Layout'
    };
    
    const lucideIconName = iconMap[iconName.toLowerCase()] || iconName;
    const pascalCase = lucideIconName
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join('');
    const Icon = (Icons as any)[pascalCase];
    return Icon ? <Icon className="h-4 w-4" /> : null;
  };
  
  const handleItemClick = (item: any, e: React.MouseEvent) => {
    if (item.label?.toLowerCase() === 'logout') {
      e.preventDefault();
      logout();
    }
  };
  
  const renderMenuItem = (item: any) => {
    const isActive = location.pathname === item.link;
    const isLogout = item.label?.toLowerCase() === 'logout';
    
    // Check if user has access to this route
    if (!isLogout && !hasRouteAccess(item.link)) {
      return null; // Hide item if no access
    }
    
    const menuItemContent = (
      <div
        className={`flex items-center px-3 py-2 rounded-lg transition-colors hover:bg-blue-50 cursor-pointer ${
          isActive ? 'bg-blue-100 text-blue-600' : 'text-gray-700 hover:text-gray-900'
        }`}
        onClick={(e) => handleItemClick(item, e)}
      >
        <span className="mr-3 flex-shrink-0">{getIcon(item.iconName)}</span>
        {!collapsed && (
          <span className="text-sm truncate">{item.label}</span>
        )}
      </div>
    );
    
    return (
      <li key={item.link || item.label}>
        {item.isLink && !isLogout ? (
          <Link to={item.link}>{menuItemContent}</Link>
        ) : (
          menuItemContent
        )}
      </li>
    );
  };
  
  const renderGroup = (group: any) => {
    // Filter accessible items
    const accessibleItems = group.items.filter((item: any) => {
      if (item.label?.toLowerCase() === 'logout') return true;
      return hasRouteAccess(item.link);
    });
    
    // Don't render group if no accessible items
    if (accessibleItems.length === 0) return null;
    
    return (
      <div key={group.label} className="mb-6">
        {!collapsed && (
          <h3 className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            {group.label}
          </h3>
        )}
        
        <ul className="space-y-1">
          {accessibleItems.map(renderMenuItem)}
        </ul>
      </div>
    );
  };

  // Handle both dynamic navigation (array of groups) and legacy navigation
  const renderNavigation = () => {
    if (!navigationItems || navigationItems.length === 0) {
      return (
        <div className="px-4 py-2 text-gray-500">No navigation items available</div>
      );
    }

    // Check if navigationItems is an array of groups (new dynamic structure)
    if (Array.isArray(navigationItems) && navigationItems[0]?.type === "group") {
      return navigationItems.map(renderGroup);
    }
    
    // Handle legacy structure or single items
    return (
      <ul className="space-y-1">
        {navigationItems.map((item: any) => {
          if (item.type === "group") {
            return renderGroup(item);
          } else {
            // Handle individual navigation item (legacy)
            if (item.label?.toLowerCase() !== 'logout' && !hasRouteAccess(item.link)) {
              return null;
            }
            return renderMenuItem(item);
          }
        })}
      </ul>
    );
  };
  
  return (
      <aside
      className={`bg-white shadow flex flex-col transition-all duration-300 border-r border-gray-200 
      ${collapsed ? 'w-16' : 'w-64'} 
      fixed left-0 top-16 bottom-0 z-30 min-h-[calc(100vh-4rem)]`}
    >
      <button
        className="flex items-center px-4 py-3 mb-1 hover:bg-gray-50 transition-colors border-b border-gray-100"
        onClick={onToggle}
      >
        <span className="text-blue-600">
          <Menu className="h-5 w-5" />
        </span>
        {!collapsed && (
          <span className="ml-3 font-semibold text-blue-600">Menu</span>
        )}
      </button>

      <nav className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto py-4 px-2 scrollbar-thin scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400">
          {renderNavigation()}
        </div>
      </nav>
    </aside>
  );
};